/** Чистые утилиты страницы товара (тестируемые, без React). */

export const mod = (i, n) => ((i % n) + n) % n

export const getApiBaseUrl = () => (import.meta.env.VITE_API_URL || '').replace('/api/v1', '')

export const buildImageSrc = (image, apiBaseUrl) => {
  if (!image || !image.image) return ''
  return image.image.startsWith('http') ? image.image : `${apiBaseUrl}${image.image}`
}

export const getMainImageSrc = (product, apiBaseUrl) => {
  if (!product?.images || !product.images.length) return ''
  const main = product.images.find((i) => i.is_main) || product.images[0]
  return buildImageSrc(main, apiBaseUrl)
}

const preloadCache = new Map()

export const preloadImage = (src, signal) => {
  if (!src) return Promise.resolve(false)
  if (preloadCache.get(src)) return Promise.resolve(true)

  return new Promise((resolve) => {
    const img = new Image()
    let done = false

    const finish = (ok) => {
      if (done) return
      done = true
      if (ok) preloadCache.set(src, true)
      resolve(ok)
    }

    if (signal?.aborted) return finish(false)

    const abortListener = () => finish(false)
    if (signal) signal.addEventListener('abort', abortListener, { once: true })

    img.onload = () => finish(true)
    img.onerror = () => finish(false)

    const decodePromise = img.decode?.()
    if (decodePromise) decodePromise.then(() => finish(true)).catch(() => finish(true))

    img.src = src
  })
}

export const isImagePreloaded = (src) => !!src && !!preloadCache.get(src)

/** Безопасное форматирование цены для отображения. */
export const formatProductPrice = (value) => {
  const n = Number(value)
  const safe = Number.isFinite(n) ? n : 0
  return new Intl.NumberFormat('ru-RU').format(safe)
}

/** Нормализация строки размера для сопоставления с таблицей порядка. */
const normSizeLabel = (s) =>
  String(s ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '')

/** Известные буквенные размеры: меньше число — левее на экране. */
const LETTER_SIZE_RANK = new Map([
  ['OS', 5],
  ['ONESIZE', 5],
  ['XXS', 10],
  ['XS', 20],
  ['S', 30],
  ['M', 40],
  ['L', 50],
  ['XL', 60],
  ['XXL', 70],
  ['2XL', 70],
  ['XXXL', 80],
  ['3XL', 80],
  ['XXXXL', 90],
  ['4XL', 90],
  ['5XL', 100],
])

const NUMERIC_SIZE_BASE = 200

/** Числовой ключ сортировки: одинаковые ключи различаются через localeCompare. */
const rankProductSizeLabel = (raw) => {
  const key = normSizeLabel(raw)
  if (LETTER_SIZE_RANK.has(key)) return LETTER_SIZE_RANK.get(key)
  if (/^\d+(\.\d+)?$/.test(key)) return NUMERIC_SIZE_BASE + Number(key)
  return 1_000_000
}

/** Сравнение двух подписей размеров (для сортировки слева направо). */
export const compareProductSizeLabels = (a, b) => {
  const ra = rankProductSizeLabel(a)
  const rb = rankProductSizeLabel(b)
  if (ra !== rb) return ra - rb
  return String(a).localeCompare(String(b), 'ru', { numeric: true, sensitivity: 'base' })
}

/** Уникальные размеры в предсказуемом порядке (M, L, XL, XXL, …). */
export const sortProductSizes = (sizes) => {
  const unique = [...new Set((sizes ?? []).filter(Boolean))]
  unique.sort(compareProductSizeLabels)
  return unique
}
